import capitalize from 'lodash/capitalize';
import compact from 'lodash/compact';
import isBoolean from 'lodash/isBoolean';

import formatPrice from '../../services/formatPrice';

/**
 * Get a human-readable string for a coverage variable
 * 1000 => $1,000
 * null => 'Not Included'
 * false => 'Not Included'
 * true => 'Included
 *
 * @param {String|Number|Boolean} val The value to format.
 * @param {String} [key] The key name.
 * @param {Object} [opts={}] The options.
 * @param {Boolean} [opts.capitalize=true] The flag that determines whether to capitalize non-number strings.
 * @return {String} The formatted string.
 */

export function getCoverageVarString(
  val?: string | boolean | null,
  key?: string | null,
  opts: { capitalize?: boolean; shouldParenthesize?: boolean } = {
    capitalize: true,
    shouldParenthesize: true,
  }
) {
  if (isBoolean(val)) {
    return val ? 'Included' : 'Not Included';
  }
  if (!val) {
    return 'Not Included';
  }
  if (/(\d+)\/?(\d+)?/.test(val as string)) {
    // handles situations where the coverage has a hyphened additional phrase i.e. $100,000/$500,000 - Reduced By
    const [coverageValue, hyphenatedStr] = val
      .toString()
      .split('-')
      .map((str) => str.trim());
    const nums = compact(
      coverageValue
        .toString()
        .match(/(\d+)\/?(\d+)?/)
        ?.slice(1)
    );
    const formattedNums = nums
      .map((el) =>
        formatPrice(el, {
          fractionDigits: 0,
          compactDisplay: hyphenatedStr ? 'short' : 'long',
          notation: hyphenatedStr ? 'compact' : 'standard',
        })
      )
      .join('/');

    if (key === 'collision_deductible') {
      return `${formattedNums} Deductible`;
    }

    if (hyphenatedStr) {
      if (opts.shouldParenthesize) {
        return `${formattedNums} (${hyphenatedStr})`;
      }
      return `${formattedNums} ${hyphenatedStr}`;
    }
    return formattedNums;
  }
  return opts.capitalize ? capitalize(val as string) : val;
}
